/**
 * A bookmark: a passage somebody has named, so a reference can point at it.
 *
 * WHY IT IS NOT A HEADING. A procedure refers to things that are not sections — a tolerance, a
 * warning, a table of torque figures halfway through a step. Making those referenceable by turning
 * them into headings would wreck the document's structure to get a pointer.
 *
 * A MARK ON A RANGE, like a comment anchor and for the same reason: it has to keep pointing at the
 * same words while people type around it, and a stored position does not survive a single edit
 * three paragraphs earlier.
 *
 * NOT INCLUSIVE: typing at the end of a bookmarked phrase is new text, not more of what was
 * bookmarked. Inclusive, a bookmark grows every time somebody adds a word beside it.
 *
 * IT IS PART OF THE DOCUMENT AND IS SIGNED. Unlike a comment anchor, which is stripped before the
 * content digest is taken, a bookmark is a structural fact about the procedure — it is what the
 * cross-references in the text resolve against, and a signature that did not cover it would be a
 * signature over a document whose references could be pointed somewhere else without trace.
 */
import { Mark, mergeAttributes } from "@tiptap/core";

export const BookmarkAnchor = Mark.create({
    name: "bookmark",
    inclusive: false,
    // SEVERAL CAN OVERLAP. A paragraph can be bookmarked and a phrase inside it bookmarked too.
    excludes: "",

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-bookmark"),
                renderHTML: (attributes) =>
                    attributes.id ? { "data-bookmark": attributes.id } : {},
            },
            name: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-bookmark-name") || "",
                renderHTML: (attributes) =>
                    attributes.name ? { "data-bookmark-name": attributes.name } : {},
            },
        };
    },

    parseHTML() {
        return [{ tag: "span[data-bookmark]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { class: "parkour-bookmark" }), 0];
    },
});

/** A new bookmark id. Never reused: an id handed to a second passage silently repoints a reference. */
export function newBookmarkId(): string {
    return `b-${Math.random().toString(36).slice(2, 10)}`;
}
